Object.assign(window.NomaCareProductFinder.prototype, {
  requestedTargets() {
    if (this.answers.category === 'bundles') {
      return NOMA_CARE_FINDER.coverageGroups[this.answers.coverage]?.targets || [];
    }

    return Array.isArray(this.answers.targets) ? this.answers.targets : [];
  },

  scoreCatalogue() {
    const selectedTargets = this.requestedTargets();
    const formatPreference = this.answers.format || 'compare';
    const compareAllBundles =
      this.answers.category === 'bundles' && this.answers.coverage === 'compare-bundles';

    const candidates = this.catalogue
      .filter((product) => {
        if (this.answers.category === 'bundles') {
          if (product.format !== 'bundle') return false;
          if (compareAllBundles) return true;
          return (
            product.groups.includes(this.answers.coverage) ||
            product.targets.some((target) => selectedTargets.includes(target))
          );
        }
        if (selectedTargets.length === 0) return true;
        return product.targets.some((target) => selectedTargets.includes(target));
      })
      .map((product) => {
        const matchedTargets = selectedTargets.filter((target) => product.targets.includes(target));
        const missingTargets = selectedTargets.filter((target) => !product.targets.includes(target));
        const extraTargets = product.targets.filter((target) => !selectedTargets.includes(target));
        const containsAll = selectedTargets.length > 0 && missingTargets.length === 0;
        const exactCoverage = containsAll && extraTargets.length === 0;
        const coverageRatio = selectedTargets.length ? matchedTargets.length / selectedTargets.length : 1;

        let score = coverageRatio * 1000;
        score -= missingTargets.length * 350;
        score -= extraTargets.length * 14;

        if (containsAll) score += 130;
        if (exactCoverage) score += 160;

        if (this.answers.category === 'bundles' && this.answers.coverage !== 'compare-bundles') {
          if (product.groups.includes(this.answers.coverage)) score += 180;
        }

        if (formatPreference !== 'compare') {
          score += product.format === formatPreference ? 120 : -40;
        }

        if (product.available) score += 10;

        if (compareAllBundles) {
          score = product.available ? 10 : 0;
        }

        return {
          product,
          score,
          matchedTargets,
          missingTargets,
          extraTargets,
          containsAll,
          exactCoverage,
        };
      })
      .sort((left, right) => {
        if (left.product.available !== right.product.available) {
          return left.product.available ? -1 : 1;
        }

        if (compareAllBundles) {
          return left.product.title.localeCompare(right.product.title, 'en');
        }
        if (right.score !== left.score) return right.score - left.score;
        if (left.missingTargets.length !== right.missingTargets.length) {
          return left.missingTargets.length - right.missingTargets.length;
        }
        if (left.extraTargets.length !== right.extraTargets.length) {
          return left.extraTargets.length - right.extraTargets.length;
        }
        return left.product.title.localeCompare(right.product.title, 'en');
      });

    return compareAllBundles ? candidates : candidates.slice(0, this.resultLimit);
  },

  matchReason(match) {
    const { product, matchedTargets, missingTargets, extraTargets, exactCoverage, containsAll } = match;
    const selectedTargets = this.requestedTargets();
    const matchedNames = matchedTargets.map((target) => NOMA_CARE_FINDER.targetLabels[target] || target);

    if (this.answers.category === 'bundles' && this.answers.coverage === 'compare-bundles') {
      return 'Included because you chose to compare all multi-target products. Available products are shown first, then products are ordered alphabetically.';
    }

    if (exactCoverage) {
      return `Its stated coverage matches all ${selectedTargets.length} selected target${selectedTargets.length === 1 ? '' : 's'} without adding other screening targets.`;
    }

    if (containsAll) {
      return `It includes all selected targets within a broader ${productFormatLabel(product).toLowerCase()}.`;
    }

    if (matchedTargets.length > 0) {
      const missingText =
        missingTargets.length > 0
          ? ` It does not include ${joinNatural(
              missingTargets.map((target) => NOMA_CARE_FINDER.targetLabels[target] || target),
            )}.`
          : '';

      return `It includes ${joinNatural(matchedNames)} from your selection.${missingText}`;
    }

    if (extraTargets.length > 0) {
      return 'Shown as another catalogue format for comparison.';
    }

    return 'Shown because its catalogue attributes overlap with your selections.';
  },

  showResults() {
    this.journey.hidden = true;
    this.results.hidden = false;
    this.resultGrid.replaceChildren();
    this.selectedForComparison.clear();
    this.currentMatches = [];
    this.closeComparison();
    this.renderCareNotice();

    if (this.answers.category === 'unsure') {
      this.resultsHeading.textContent = 'Browse NomaCare by health area';
      this.selectionSummary.textContent = 'You selected: browse by category.';
      this.resultsExplanation.textContent =
        'No medical information is needed to browse these catalogue categories.';
      NOMA_CARE_FINDER.collections.forEach((collection) => {
        this.resultGrid.append(this.createCollectionCard(collection));
      });
      this.compareBar.hidden = true;
    } else {
      this.currentMatches = this.scoreCatalogue();
      const compareAllBundles =
        this.answers.category === 'bundles' && this.answers.coverage === 'compare-bundles';
      const hasCompleteCoverage = this.currentMatches.some((match) => match.containsAll);
      const hasPartialMatches =
        !compareAllBundles &&
        this.requestedTargets().length > 0 &&
        this.currentMatches.length > 0 &&
        !hasCompleteCoverage;

      this.resultsHeading.textContent =
        this.currentMatches.length === 0
          ? 'No published match yet'
          : hasPartialMatches
            ? 'Partial catalogue matches'
            : this.currentMatches.length === 1
              ? 'Catalogue match'
              : 'Products to compare';
      this.selectionSummary.textContent = this.buildSelectionSummary();
      this.resultsExplanation.textContent = compareAllBundles
        ? 'All currently published multi-target products are shown. Available products appear first, then products are ordered alphabetically. This is a catalogue comparison, not a medical recommendation.'
        : hasPartialMatches
          ? 'No published product covers every selected target. The products below overlap with part of your selection; review the missing targets shown on each card. This is a catalogue comparison, not a medical recommendation.'
          : 'These products are ordered by stated target coverage and your format preference. The ordering is a catalogue match, not a medical recommendation.';

      if (this.currentMatches.length === 0) {
        this.resultGrid.append(this.createEmptyState());
        this.compareBar.hidden = true;
      } else {
        this.currentMatches.forEach((match, index) => {
          this.resultGrid.append(this.createProductCard(match, index));
        });
        this.compareBar.hidden = this.currentMatches.length < 2;
        this.updateCompareState();
      }
    }

    requestAnimationFrame(() => {
      this.resultsHeading.focus({ preventScroll: true });
      this.results.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'start',
      });
    });
  },

  buildSelectionSummary() {
    const parts = [NOMA_CARE_FINDER.categoryLabels[this.answers.category]];

    if (this.answers.category === 'bundles') {
      const coverage = NOMA_CARE_FINDER.coverageGroups[this.answers.coverage]?.label;
      if (coverage) parts.push(coverage);
    } else {
      const targetNames = this.requestedTargets().map(
        (target) => NOMA_CARE_FINDER.targetLabels[target] || target,
      );
      if (targetNames.length) parts.push(joinNatural(targetNames));
      if (this.answers.format) parts.push(NOMA_CARE_FINDER.formatLabels[this.answers.format]);
    }

    if (this.answers.context) parts.push(NOMA_CARE_FINDER.contextLabels[this.answers.context]);

    return `You selected: ${parts.filter(Boolean).join(' · ')}.`;
  },

  renderCareNotice() {
    const context = this.answers.context;

    if (!context || context === 'routine' || this.answers.category === 'unsure') {
      this.careNotice.hidden = true;
      this.careNotice.removeAttribute('data-tone');
      return;
    }

    this.careNotice.hidden = false;

    if (context === 'recent') {
      this.careNotice.dataset.tone = 'timing';
      this.careTitle.textContent = 'Timing can affect whether an infection is detectable.';
      this.careCopy.textContent =
        'A very recent exposure may fall within a detection window. Review the instructions and limitations for the specific product and ask a qualified healthcare professional when to test or retest.';
      return;
    }

    this.careNotice.dataset.tone = 'urgent';
    this.careTitle.textContent = 'Symptoms or a medical concern need professional assessment.';
    this.careCopy.textContent =
      'This Product Finder cannot assess symptoms, determine urgency, or choose a medically appropriate test. Seek advice from a qualified healthcare professional, especially for severe symptoms, pregnancy-related concerns, or urgent exposure questions.';
  }
});
